import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, User, MessageSquare, Plus, Search, ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const ChatPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [contacts, setContacts] = useState([]);
    const [showNewChat, setShowNewChat] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);
    const pollInterval = useRef(null);

    useEffect(() => {
        fetchConversations();
        fetchContacts();
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Poll for new messages every 5 seconds when a conversation is active
    useEffect(() => {
        if (activeConversation) {
            pollInterval.current = setInterval(() => {
                fetchMessages(activeConversation.id, true);
            }, 5000);
        } else {
            if (pollInterval.current) clearInterval(pollInterval.current);
        }
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [activeConversation]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const fetchConversations = async () => {
        try {
            const res = await api.get('/chat/conversations');
            setConversations(res.data.data || []);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (conversationId, silent = false) => {
        try {
            const res = await api.get(`/chat/conversations/${conversationId}/messages`);
            if (res.data.data) {
                setMessages(res.data.data);
                // Mark as read
                await api.put(`/chat/conversations/${conversationId}/read`);
            }
        } catch (error) {
            if (!silent) console.error('Error fetching messages:', error);
        }
    };

    const fetchContacts = async () => {
        try {
            const res = await api.get('/chat/contacts');
            setContacts(res.data.data || []);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const selectConversation = async (conversation) => {
        setActiveConversation(conversation);
        setMessages([]);
        await fetchMessages(conversation.id);
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!messageInput.trim() || !activeConversation || sending) return;

        setSending(true);
        try {
            const res = await api.post(`/chat/conversations/${activeConversation.id}/messages`, {
                message: messageInput.trim()
            });

            if (res.data.success) {
                setMessages(prev => [...prev, res.data.data]);
                setMessageInput('');
                // Refresh conversations to update last message
                fetchConversations();
                scrollToBottom();
            }
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    const startDirectConversation = async (contactId) => {
        try {
            const res = await api.post('/chat/direct', { user_id: contactId });
            if (res.data.success) {
                setShowNewChat(false);
                await fetchConversations();
                selectConversation(res.data.data);
            } else {
                console.error('Failed to start conversation:', res.data);
                alert('Failed to start conversation: ' + (res.data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error starting conversation:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Connection error';
            alert('Could not start chat: ' + errorMsg);
        }
    };


    const getOtherParticipants = (conversation) => {
        if (!conversation?.participants) return [];
        return conversation.participants.filter(p => p.id !== user?.id);
    };

    const getConversationName = (conversation) => {
        if (conversation.title) return conversation.title;
        const others = getOtherParticipants(conversation);
        return others.map(p => `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email).join(', ') || 'Conversation';
    };

    const getParticipantAvatar = (participant) => {
        return `${participant.first_name?.[0] || ''}${participant.last_name?.[0] || ''}`.trim() || '?';
    };

    const filteredContacts = contacts.filter(c => {
        const name = `${c.first_name} ${c.last_name} ${c.email} ${c.role}`.toLowerCase();
        return name.includes(searchQuery.toLowerCase());
    });

    // Group contacts by role
    const groupedContacts = {
        landlord: filteredContacts.filter(c => c.role === 'landlord'),
        caretaker: filteredContacts.filter(c => c.role === 'caretaker'),
        tenant: filteredContacts.filter(c => c.role === 'tenant'),
    };

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="h-[calc(100vh-2rem)] flex bg-white rounded-xl shadow-sm border overflow-hidden">
            {/* Conversations List */}
            <div className="w-80 border-r flex flex-col bg-gray-50">
                <div className="p-4 border-b bg-white">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
                        <button
                            onClick={() => setShowNewChat(!showNewChat)}
                            className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            <Plus className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>

                {/* New Chat Contact List */}
                {showNewChat && (
                    <div className="flex-1 overflow-y-auto p-3">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-gray-700">Select Contact</h3>
                            <button onClick={() => setShowNewChat(false)} className="text-xs text-gray-500 hover:text-gray-700">
                                Cancel
                            </button>
                        </div>
                        {Object.entries(groupedContacts).map(([role, roleContacts]) => 
                            roleContacts.length > 0 && (
                                <div key={role} className="mb-4">
                                    <p className="text-xs font-medium text-gray-500 uppercase mb-2 px-2">{role}s</p>
                                    {roleContacts.map(contact => (
                                        <button
                                            key={contact.id}
                                            onClick={() => startDirectConversation(contact.id)}
                                            className="w-full flex items-center p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-medium">
                                                {getParticipantAvatar(contact)}
                                            </div>
                                            <div className="ml-3 text-left flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{contact.first_name} {contact.last_name}</p>
                                                <p className="text-xs text-gray-500 truncate capitalize">{contact.role}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )
                        )}
                        {filteredContacts.length === 0 && (
                            <p className="text-center text-sm text-gray-500 py-8">No contacts found</p>
                        )}
                    </div>
                )}

                {/* Conversation List */}
                {!showNewChat && (
                    <div className="flex-1 overflow-y-auto">
                        {conversations.length > 0 ? conversations.map(conv => (
                            <button
                                key={conv.id}
                                onClick={() => selectConversation(conv)}
                                className={`w-full p-3 border-b hover:bg-gray-100 transition-colors text-left ${
                                    activeConversation?.id === conv.id ? 'bg-indigo-50' : ''
                                }`}
                            >
                                <div className="flex items-center">
                                    <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center text-indigo-700 text-sm font-medium flex-shrink-0">
                                        {getConversationName(conv).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="ml-3 flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{getConversationName(conv)}</p>
                                        <p className="text-xs text-gray-500 truncate mt-0.5">
                                            {conv.last_message || 'No messages yet'}
                                        </p>
                                    </div>
                                    {conv.last_message_at && (
                                        <span className="text-xs text-gray-400 ml-2">
                                            {new Date(conv.last_message_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            </button>
                        )) : (
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                                <MessageSquare className="h-12 w-12 text-gray-300 mb-3" />
                                <p className="text-sm text-gray-500 text-center">No conversations yet</p>
                                <p className="text-xs text-gray-400 text-center mt-1">Click + to start a new chat</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
                {activeConversation ? (
                    <>
                        {/* Chat Header */}
                        <div className="p-4 border-b bg-white flex items-center">
                            <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center text-indigo-700 text-sm font-medium">
                                {getConversationName(activeConversation).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="ml-3">
                                <p className="font-medium text-gray-900">{getConversationName(activeConversation)}</p>
                                <p className="text-xs text-gray-500">
                                    {getOtherParticipants(activeConversation).map(p => p.role).join(', ')}
                                </p>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                            {messages.length > 0 ? messages.map((msg, index) => {
                                const isOwn = msg.sender_id === user?.id;
                                return (
                                    <div key={msg.id || index} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[70%] ${isOwn ? 'order-1' : 'order-1'}`}>
                                            {!isOwn && (
                                                <p className="text-xs text-gray-500 mb-1 ml-1">
                                                    {msg.first_name} {msg.last_name}
                                                </p>
                                            )}
                                            <div className={`rounded-2xl px-4 py-2 ${
                                                isOwn 
                                                    ? 'bg-indigo-600 text-white rounded-br-md' 
                                                    : 'bg-white text-gray-900 border rounded-bl-md'
                                            }`}>
                                                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                            </div>
                                            <p className={`text-xs text-gray-400 mt-1 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
                                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-sm text-gray-400">No messages yet. Start the conversation!</p>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Message Input */}
                        <div className="p-4 border-t bg-white">
                            <form onSubmit={sendMessage} className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    value={messageInput}
                                    onChange={(e) => setMessageInput(e.target.value)}
                                    placeholder="Type a message..."
                                    className="flex-1 px-4 py-2.5 border rounded-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    disabled={sending}
                                />
                                <button
                                    type="submit"
                                    disabled={!messageInput.trim() || sending}
                                    className="p-2.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="h-5 w-5" />
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center bg-gray-50">
                        <div className="text-center">
                            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-600">Your Messages</h3>
                            <p className="text-sm text-gray-400 mt-1">Select a conversation or start a new chat</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatPage;
